import React, {
  useState, useEffect, useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFirestore } from 'firebaseHooks/FirestoreContext';
import {
  doc,
  getDoc,
} from 'firebase/firestore';
import useGlobal from 'global-state/store';
import {
  CircularProgress,
} from '@mui/material';
import InventoryItems from './InventoryItems';
import OfflineIndicator from './components/OfflineIndicator';
import InventoryStorageGuard from './components/InventoryStorageGuard';
import { useInventoryQueueRegistry } from './hooks/useInventoryQueueRegistry';

export default function Inventory() {
  const location = useLocation();

  const navigate = useNavigate();

  const inventoryId = location.state?.inventory;

  useEffect(() => {
    if (inventoryId === undefined) {
      navigate('/');
    }
  }, [inventoryId, navigate]);

  if (!inventoryId) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh',
      }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <InventoryStorageGuard>
      <InventoryWithLocation inventoryId={inventoryId} />
    </InventoryStorageGuard>
  );
}

function InventoryWithLocation({ inventoryId }) {
  const [inventoryDetails, setInventoryDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const db = useFirestore();
  const [globalState] = useGlobal();
  const queueRegistry = useInventoryQueueRegistry();

  const inventoryRef = React.useMemo(
    () => doc(
      db,
      `organizations/${globalState.activeOrganization}/apps/inventory/inventories`,
      inventoryId,
    ),
    [db, globalState.activeOrganization, inventoryId],
  );

  const localUpdate = globalState.localInventoryUpdates[inventoryId];

  const fetchInventory = React.useCallback(async () => {
    const inventorySnapshot = await getDoc(inventoryRef);

    const firestoreData = { id: inventorySnapshot.id, ...inventorySnapshot.data() };
    const finalData = localUpdate ? { ...firestoreData, ...localUpdate } : firestoreData;

    setInventoryDetails(finalData);

    setLoading(false);
  }, [inventoryRef, localUpdate]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  // Flush on unmount when leaving this inventory view
  const queueRef = useRef(queueRegistry);
  const inventoryIdRef = useRef(inventoryId);

  useEffect(() => {
    queueRef.current = queueRegistry;
  }, [queueRegistry]);

  useEffect(() => {
    const previousId = inventoryIdRef.current;
    if (previousId && previousId !== inventoryId && queueRegistry) {
      queueRegistry.flush(previousId);
    }
    inventoryIdRef.current = inventoryId;
  }, [inventoryId, queueRegistry]);

  useEffect(() => () => {
    const registry = queueRef.current;
    const invId = inventoryIdRef.current;
    if (!registry || !invId) return;
    const nextPath = window.location?.pathname || '';
    if (!nextPath.startsWith('/inventory')) {
      registry.flush(invId);
    }
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh',
      }}
      >
        <CircularProgress />
      </div>
    );
  }
  return (
    <div>
      <OfflineIndicator networkState={globalState.networkState} />
      <InventoryItems inventoryDetails={inventoryDetails} setInventoryDetails={setInventoryDetails} />
    </div>
  );
}
